/* Custo Options Engine — storefront behaviour (options-engine/SPEC.md).
 * The Liquid snippet renders form-associated inputs; this file handles:
 *  - toggle show/hide (paid name, charm conditional reveal)
 *  - character counters
 *  - required-field gate on ATC submit (incl. charm-On => charm chosen)
 *  - _itemKey generation + _addOnVariant stamping
 *  - add-on billing lines (charm ₹50 / paid name ₹100 / CT add-ons) via /cart/add.js
 *    with _parentKey pairing — same schema Tepo emits (SPEC §2).
 */
(function () {
  'use strict';
  var root = document.getElementById('custo-options');
  if (!root || root.dataset.custoInit) return;
  root.dataset.custoInit = '1';

  var formId = root.dataset.formId;
  var form = document.getElementById(formId);
  if (!form) return;

  var CHARM_VARIANT = root.dataset.charmVariant || '';
  var NAME_VARIANT = root.dataset.nameVariant || '';
  var NAME_REQUIRED = root.dataset.nameRequired === 'true';

  /* ---------- toggles + counters ---------- */
  function fieldFor(kind, unit) {
    return root.querySelector('[data-custo-' + kind + '-field][data-unit="' + unit + '"]');
  }

  function syncToggle(t) {
    var panel = fieldFor(t.dataset.custoToggle, t.dataset.unit);
    if (panel) {
      panel.classList.toggle('custo-hidden', !t.checked);
      panel.querySelectorAll('input,select').forEach(function (c) { c.disabled = !t.checked; });
    }
  }
  /* swatch names are dot-only (sr-only in CSS); the chosen name shows in the
     legend as "Colour 1: <name>" once a swatch is picked (Rohan 12-08-26) */
  function syncColourLegend(fs) {
    var chosen = fs.querySelector('[data-custo-colour-chosen]');
    if (!chosen) return;
    var sel = fs.querySelector('input[type="radio"]:checked');
    chosen.textContent = sel ? ': ' + sel.value : '';
  }
  function syncAll() {
    root.querySelectorAll('[data-custo-toggle]').forEach(syncToggle);
    root.querySelectorAll('.custo-colours').forEach(syncColourLegend);
  }
  /* browsers restore checkbox state on back-navigation (bfcache) without firing
     change — resync at init and on every pageshow */
  syncAll();
  window.addEventListener('pageshow', syncAll);

  root.addEventListener('change', function (e) {
    if (e.target.matches('[data-custo-toggle]')) syncToggle(e.target);
    if (e.target.matches('.custo-colours input[type="radio"]')) syncColourLegend(e.target.closest('.custo-colours'));
    clearError();
  });

  /* ---------- add-on cards (pill-language grid) ----------
     Tap a card = select (mint fill, like a pack pill); tap again = deselect. The
     stepper only exists inside a selected card, so its buttons are checked first. */
  root.addEventListener('click', function (e) {
    var minus = e.target.closest('[data-custo-qty-minus]');
    var plus = e.target.closest('[data-custo-qty-plus]');
    if (minus || plus) {
      var row = (minus || plus).closest('[data-custo-addon-qty]');
      var q = parseInt(row.dataset.qty, 10) || 0;
      /* single-choice cards (battery) keep qty >= 1 — the card tap gates adding;
         colour cards step down to 0, which deselects */
      var floor = row.dataset.on !== undefined ? 1 : 0;
      q = plus ? q + 1 : Math.max(floor, q - 1);
      row.dataset.qty = q;
      row.querySelector('.custo-qty-val').textContent = q;
      if (row.dataset.on === undefined) row.classList.toggle('custo-active', q > 0);
      return;
    }
    var card = e.target.closest('[data-custo-addon-card]');
    if (!card) return;
    if (card.dataset.on !== undefined) {
      var on = card.dataset.on === '1' ? '0' : '1';
      card.dataset.on = on;
      card.classList.toggle('custo-active', on === '1');
    } else {
      var cq = parseInt(card.dataset.qty, 10) || 0;
      card.dataset.qty = cq > 0 ? 0 : 1;
      card.querySelector('.custo-qty-val').textContent = card.dataset.qty;
      card.classList.toggle('custo-active', cq === 0);
    }
  });

  root.addEventListener('input', function (e) {
    if (e.target.matches('[data-custo-name]')) {
      var counter = e.target.parentElement.querySelector('[data-custo-count]');
      if (counter) counter.textContent = e.target.value.length;
      e.target.classList.remove('custo-invalid');
    }
  });

  /* ---------- validation ---------- */
  var errorBox = root.querySelector('[data-custo-error]');
  function showError(msg) {
    if (errorBox) { errorBox.textContent = msg; errorBox.classList.remove('custo-hidden'); }
  }
  function clearError() {
    if (errorBox) errorBox.classList.add('custo-hidden');
    root.querySelectorAll('.custo-invalid').forEach(function (n) { n.classList.remove('custo-invalid'); });
    root.querySelectorAll('.custo-missing').forEach(function (n) { n.classList.remove('custo-missing'); });
  }

  function validate() {
    clearError();
    var bad = [];
    /* colour radios: one checked per rendered group */
    root.querySelectorAll('.custo-colours').forEach(function (fs) {
      if (!fs.querySelector('input:checked')) { bad.push(fs); fs.classList.add('custo-missing'); }
    });
    /* names: required when always-on (name_required) or when their toggle is On */
    root.querySelectorAll('[data-custo-name]').forEach(function (inp) {
      if (inp.disabled) return;                       /* toggle Off -> not submitted */
      var toggled = root.querySelector('[data-custo-toggle="name"][data-unit="' + inp.dataset.unit + '"]');
      var required = toggled ? toggled.checked : NAME_REQUIRED;
      if (required && !inp.value.trim()) { bad.push(inp); inp.classList.add('custo-invalid'); }
    });
    /* charm On => selection required (fixes Tepo's 116 orphaned-On lines).
       Keyed off the TOGGLE, not the select's disabled state, so a desynced
       panel can never slip through. */
    root.querySelectorAll('[data-custo-toggle="charm"]:checked').forEach(function (t) {
      var sel = root.querySelector('[data-custo-charm][data-unit="' + t.dataset.unit + '"]');
      if (sel && !sel.value) { bad.push(sel); sel.classList.add('custo-invalid'); }
    });
    if (bad.length) {
      showError('Some fields are required');
      /* instant, not smooth — the page runs Corner Resize/MutationObservers that can
         livelock during a long smooth-scroll animation (freeze seen live 11-08-26) */
      (bad[0].scrollIntoView ? bad[0] : root).scrollIntoView({ block: 'center' });
      return false;
    }
    return true;
  }

  /* ---------- add-on billing lines ---------- */
  function addonLines(itemKey) {
    var lines = [];
    var paidCharms = 0;
    root.querySelectorAll('[data-custo-toggle="charm"]:checked').forEach(function (t) {
      var sel = root.querySelector('[data-custo-charm][data-unit="' + t.dataset.unit + '"]');
      var opt = sel && sel.selectedOptions[0];
      if (sel && sel.value && opt && opt.dataset.paid) paidCharms++;
    });
    if (paidCharms && CHARM_VARIANT) {
      lines.push({ id: Number(CHARM_VARIANT), quantity: paidCharms, properties: { _parentKey: itemKey } });
    }
    var paidNames = root.querySelectorAll('[data-custo-toggle="name"]:checked').length;
    if (paidNames && NAME_VARIANT) {
      lines.push({ id: Number(NAME_VARIANT), quantity: paidNames, properties: { _parentKey: itemKey } });
    }
    /* CT add-ons (v3-LP layout): qty state lives in data attributes. The lines post
       WITHOUT _parentKey on purpose — opsapp's monthly Excel drops _parentKey lines
       from product counts (monthly_orders._is_addon) and these are real physical
       products (cases; the pick list keeps them by title either way), and the
       _addOnVariant billing reconciliation stays paired-only. But bare lines were
       invisible to the cart's parent linkage: the case rendered as an independent
       unlocked row that survived parent removal (found 13-08-26). _ccParent is the
       cart-only pairing key — cart-items.liquid + cart-custo.js treat it exactly
       like _parentKey (fold into the parent as a chip, lock, orphan-sweep); ops
       knows nothing about it. Simple add-ons (battery) gate on data-on; colour
       rows on qty > 0. */
    root.querySelectorAll('[data-custo-addon-qty]').forEach(function (el) {
      if (el.dataset.on !== undefined && el.dataset.on !== '1') return;
      var q = parseInt(el.dataset.qty, 10) || 0;
      if (q > 0 && el.dataset.variant) lines.push({ id: Number(el.dataset.variant), quantity: q, properties: { _ccParent: itemKey } });
    });
    return lines;
  }

  /* ---------- collect visible properties (for the Corner middleware path) ---------- */
  function collectProps() {
    var props = {};
    root.querySelectorAll('input[name^="properties["], select[name^="properties["]').forEach(function (el) {
      if (el.disabled) return;
      if (el.type === 'radio' && !el.checked) return;
      var key = el.name.slice(11, -1);
      if (key.charAt(0) === '_') return;                      /* plumbing handled separately */
      if (el.type === 'checkbox') { props[key] = el.checked ? 'On' : props[key] || 'Off'; return; }
      if (el.type === 'hidden' && el.value === 'Off') { if (!(key in props)) props[key] = 'Off'; return; }
      props[key] = el.value;
    });
    return props;
  }
  function genKey() {
    return 'cst' + Math.random().toString(16).slice(2, 12) + Date.now().toString(36);
  }
  function currentVariantId() {
    var el = form.querySelector('[name="id"]');
    return el ? String(el.value) : '';
  }
  var OPTION_SET_ID = (root.querySelector('input[name="properties[_optionSetId]"]') || {}).value || '';

  /* Stamp _itemKey eagerly: some ATC paths serialize the form directly (no submit
     event, no fetch we can wrap), and a disabled hidden input never submits. With
     an always-enabled pre-filled key, every path carries it. Rotated after each
     successful add so two adds never share a key. */
  var keyInput = root.querySelector('[data-custo-itemkey]');
  function stampKey() {
    keyInput.value = genKey();
    keyInput.disabled = false;
    return keyInput.value;
  }
  stampKey();

  /* ---------- Corner sticky ATC ----------
     The visible ATC on this theme is Corner's button, which POSTs the bare variant
     via its own fetch with NO properties. Guard the click (validation) and enrich
     Corner's request body in-flight (properties + add-on lines, atomically). */
  /* window capture, not document — same precedence reason as the theme-ATC guard */
  window.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('#corner-sticky-atc-cta-button, [id^="corner-"][id*="atc"]')) {
      if (!validate()) { e.preventDefault(); e.stopImmediatePropagation(); }
    }
  }, true);

  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    var deferAddonKey = null;
    try {
      if (/\/cart\/add(\.js)?/.test(String(input && input.url || input)) && init && init.body) {
        if (typeof init.body === 'string' && /^\s*[\[{]/.test(init.body)) {
          /* JSON body (Corner's fetch path): enrich in-flight */
          var payload = JSON.parse(init.body);
          var items = payload.items || (payload.id ? [payload] : null);
          if (items) {
            var cur = currentVariantId();
            var main = null;
            for (var i = 0; i < items.length; i++) {
              var p = items[i].properties || {};
              if (String(items[i].id) === cur && !p._itemKey && !p._parentKey) { main = items[i]; break; }
            }
            if (main) {
              if (!validate()) return Promise.reject(new Error('custo-options: required fields missing'));
              var key = genKey();
              main.properties = Object.assign({}, main.properties, collectProps(),
                { _itemKey: key, _optionSetId: OPTION_SET_ID });
              var lines = addonLines(key);
              if (lines.length) {
                main.properties._addOnVariant = lines.map(function (l) { return l.id + ':' + l.quantity + ':0'; }).join(',');
                items.push.apply(items, lines);
              }
              if (payload.items) payload.items = items;
              init = Object.assign({}, init, { body: JSON.stringify(payload) });
            }
          }
        } else if (bodyCarriesKey(init.body, keyInput.value)) {
          /* form-serialized fetch add carrying our key (defensive backstop —
             historically Tepo's overlap ATC, kept for any widget that posts the
             theme form over fetch). */
          if (!validate()) return Promise.reject(new Error('custo-options: required fields missing'));
          deferAddonKey = keyInput.value;
          setTimeout(stampKey, 0);
        }
      }
    } catch (err) { /* never break the store's cart flow */ }
    var out = origFetch.call(this, input, init);
    if (deferAddonKey && out && typeof out.then === 'function') {
      /* fire the paired add-on line only AFTER the parent add lands — the cart's
         orphan sweeper deletes a _parentKey line whose parent isn't in the cart
         yet (race under-billed 5 real orders on 13-08-26) */
      var dk = deferAddonKey;
      out.then(function (res) { if (res && res.ok) fireAddons(dk); }).catch(function () {});
    }
    return out;
  };

  /* Stamp _addOnVariant on the form NOW so any serializer (submit POST, theme XHR)
     carries it; fireAddons() is deduped per _itemKey because up to three hooks can
     see the same add (click guard, submit, XHR wrapper). */
  var avInput = root.querySelector('[data-custo-addonvariant]');
  function prepFormLines(key) {
    var lines = addonLines(key);
    /* _addOnVariant reconciles PAIRED billing lines (charm / paid name) only —
       bare CT add-on lines are ordinary items and stay out of it */
    var paired = lines.filter(function (l) { return l.properties && l.properties._parentKey; });
    if (paired.length) {
      avInput.value = paired.map(function (l) { return l.id + ':' + l.quantity + ':0'; }).join(',');
      avInput.disabled = false;
    } else {
      avInput.value = '';
      avInput.disabled = true;
    }
    return lines;
  }
  var firedKeys = {};
  function fireAddons(key) {
    if (firedKeys[key]) return;
    firedKeys[key] = 1;
    var lines = addonLines(key);
    if (!lines.length) return;
    /* keepalive — the theme ATC may be a native POST navigation (cart_type: page),
       which would kill any deferred timer/plain fetch. */
    fetch('/cart/add.js', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines }),
    }).then(function () {
      document.dispatchEvent(new CustomEvent('cart:updated'));
    }).catch(function () { /* main line still carries _addOnVariant; ops reconciles */ });
  }

  /* ---------- theme ATC button (capture: runs before <product-form>'s handler) ----------
     The theme's own buy button never fires a submit event — <product-form> intercepts
     the click and serializes the form over XMLHttpRequest, which the submit hook and
     the fetch wrapper both miss (found live 11-08-26: nameless cardholder in cart). */
  /* On WINDOW, not document: Corner registers its own document-capture click
     handler before our deferred script runs, and same-node capture listeners fire
     in registration order — but window capture always fires before document, so
     this guard wins regardless of load order. Without it Corner starts its ATC
     flow (dims the page, fires the XHR) before we can block. */
  window.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('button[type="submit"], button[name="add"]');
    if (!btn || btn.form !== form) return;
    if (!validate()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    prepFormLines(keyInput.value || stampKey());
  }, true);

  /* ---------- XHR backstop (mirror of the fetch wrapper) ----------
     Catches any serializer that bypasses the click guard. Only acts on bodies that
     carry OUR eager _itemKey — other products/widgets pass through untouched. */
  /* True when the serialized body carries OUR current _itemKey. Must scan every
     entry, not .get('properties[_itemKey]'): Tepo's overlap ATC re-nests all form
     fields under items[0][...], so the key arrives as
     items[0][properties[_itemKey]] (found live 11-08-26). */
  function bodyCarriesKey(body, key) {
    if (!key) return false;
    if (body instanceof FormData || body instanceof URLSearchParams) {
      var found = false;
      body.forEach(function (v, k) {
        if (v === key && k.indexOf('properties[_itemKey') !== -1) found = true;
      });
      return found;
    }
    if (typeof body === 'string') {
      var re = /properties(?:%5B|\[)_itemKey[^=&]*=([^&]*)/g, m;
      while ((m = re.exec(body))) { if (decodeURIComponent(m[1]) === key) return true; }
    }
    return false;
  }
  var origOpen = XMLHttpRequest.prototype.open;
  var origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._custoUrl = String(url);
    return origOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    try {
      if (/\/cart\/add/.test(this._custoUrl || '') && bodyCarriesKey(body, keyInput.value)) {
        if (!validate()) {
          /* Do NOT swallow silently — a hung XHR leaves the caller (Corner dims the
             whole page) waiting forever. Re-point at a 404 so its error path runs. */
          origOpen.call(this, 'POST', '/custo-options-blocked');
          return origSend.call(this, null);
        }
        /* fire the paired add-on line only AFTER the parent add lands (loadend,
           2xx): the engine used to post it first, and the cart's orphan sweeper
           deletes a _parentKey line whose parent isn't in the cart yet — the race
           silently under-billed 5 real orders on 13-08-26. Skipped on non-2xx:
           a failed parent add must not leave a paid add-on behind. */
        var addKey = keyInput.value;
        this.addEventListener('loadend', function () {
          if (this.status >= 200 && this.status < 300) fireAddons(addKey);
        });
        setTimeout(stampKey, 0);
      }
    } catch (err) { /* never break the store's cart flow */ }
    return origSend.apply(this, arguments);
  };

  /* ---------- submit hook (capture, runs before the theme's handler) ----------
     Whether this add is a navigation or an AJAX fetch is the THEME's call:
     <product-form> binds its submit->fetch handler only when it carries the
     `cart-popup` attribute, which snippets/product-form.liquid emits whenever
     settings.cart_type != 'page'. That setting flipped to `drawer` on 13-08-26.

     No cart-popup (native POST): the page is about to navigate, so fire the paired
     billing line NOW with keepalive — the navigation serialises the two adds and
     nothing can render in between.

     cart-popup (AJAX): firing here posts the billing line BEFORE the parent and the
     two /cart/add.js writes race — and because fireAddons() is deduped per key, this
     immediate call also swallowed the deferred fire the fetch wrapper was supposed to
     make, so the 13-08 race fix never applied to this path at all. Leave it to the
     wrapper, which fires only once the parent add has resolved. */
  var productForm = form.closest && form.closest('product-form');
  form.addEventListener('submit', function (e) {
    if (!validate()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    var key = keyInput.value || stampKey();
    prepFormLines(key);
    if (!(productForm && productForm.hasAttribute('cart-popup'))) {
      fireAddons(key);
    } else {
      /* backstop: if no wrapper claimed this add (theme changes transport again),
         post it late rather than hand out free personalisation. The parent has long
         landed by then, so the line can never read as an orphan. Deduped, so it is
         a no-op whenever the wrapper already fired. */
      setTimeout(function () { fireAddons(key); }, 3000);
    }
    /* rotate so a second add gets its own key (only matters on ajax carts) */
    setTimeout(stampKey, 0);
  }, true);
})();
